import { useState } from 'react'
import { useNorseTerm } from './NorseTermData'
import { Upload } from 'lucide-react'
import type { NtCourse, NtStudent, NtTerm } from '../../core/types'
import { parseStudentCsv, importStudents } from './model'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Student import — the roster and what each of them ranked.
 *
 * A file, not a form. The rankings already exist as a Google Form export the
 * day the deadline closes, and asking the office to retype 240 rows into a web
 * page would be the single worst thing this app could do to them.
 *
 * Course columns hold the course *title*, because that is what the Form writes
 * and what a human can proof-read. Titles that do not match a course are
 * reported before anything is written — a silently dropped ranking is a
 * student who mysteriously got none of their choices, and nobody would ever
 * trace it back here.
 */
export function Students({ term, courses, students }: {
  term: NtTerm
  courses: NtCourse[]
  students: NtStudent[] | null
}) {
  const [preview, setPreview] = useState<ReturnType<typeof parseStudentCsv> | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<number | null>(null)

  const read = (file: File) => {
    const r = new FileReader()
    r.onload = () => { setPreview(parseStudentCsv(String(r.result), courses)); setDone(null) }
    r.readAsText(file)
  }

  const commit = async () => {
    if (!preview?.rows.length) return
    setBusy(true); setErr(null)
    try {
      const n = await importStudents(term.id, preview.rows)
      setDone(n); setPreview(null)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const withoutRanks = (students ?? []).filter((s) => s.ranks.length === 0).length

  const { selections } = useNorseTerm()
  const submitted = selections.filter((x) => !!x.submittedAt).length
  return (
    <>
      {/* Students choose in the app; the import is the fallback. */}
      <div className="notice notice--info" style={{ marginTop: 'var(--s-2)' }}>
        <span aria-hidden="true">i</span>
        <div>
          <strong>{submitted} student{submitted === 1 ? ' has' : 's have'} submitted course selections in SailNorse.</strong>{' '}
          Placement uses those automatically, with the time each was first submitted. Import a CSV below only for
          students who chose on a paper or Google form; anyone who chose in the app is taken from the app.
        </div>
      </div>
      <section className="card" style={{ marginTop: 'var(--s-2)' }}>
        <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-1)' }}>Import the student list</h3>
        <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '0 0 var(--s-3)' }}>
          A CSV with a header row: <code>Timestamp, studentId, name, grade, rank1, rank2, …</code>.
          The course selection columns hold course titles exactly as they appear in the catalog. Keep the form&apos;s{' '}
          <code>Timestamp</code> column. It decides who is placed first when placement is first come,
          first served.
        </p>

        <label className="plan__add" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <Upload size={16} aria-hidden="true" />
          Choose a CSV file
          <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) read(f) }} />
        </label>

        {err && <ErrorState body={`Import failed. ${err}`} />}
        {done !== null && (
          <div className="notice notice--info" style={{ marginTop: 'var(--s-3)' }}>
            <span aria-hidden="true">✓</span>
            <div>{done} students imported. The previous student list for this term was replaced.</div>
          </div>
        )}

        {preview && (
          <div style={{ marginTop: 'var(--s-3)' }}>
            {preview.error ? <ErrorState body={preview.error} /> : (
              <>
                <div className="dl">
                  <div><div className="dl__k">Rows</div><div className="dl__v">{preview.rows.length}</div></div>
                  <div><div className="dl__k">With course selections</div>
                    <div className="dl__v">{preview.rows.filter((r) => r.ranks.length).length}</div></div>
                  <div><div className="dl__k">Unmatched titles</div>
                    <div className="dl__v">{preview.unmatched.length}</div></div>
                  <div><div className="dl__k">With a timestamp</div>
                    <div className="dl__v">{preview.rows.filter((r) => r.submittedAt).length}</div></div>
                </div>

                {/* First-come placement needs these; say so before the write. */}
                {preview.rows.length > 0 && preview.rows.every((r) => !r.submittedAt) && preview.badTimestamps.length === 0 && (
                  <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
                    <span aria-hidden="true">⚠</span>
                    <div>
                      <strong>No Timestamp column.</strong> First-come placement cannot tell who submitted first,
                      so every student would be treated as tied. Export again with the form&apos;s Timestamp column.
                    </div>
                  </div>
                )}
                {preview.badTimestamps.length > 0 && (
                  <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
                    <span aria-hidden="true">⚠</span>
                    <div>
                      <strong>These timestamps could not be read, so these students will be placed last:</strong>{' '}
                      {preview.badTimestamps.join(', ')}
                    </div>
                  </div>
                )}

                {/* Reported before the write, never after. */}
                {preview.unmatched.length > 0 && (
                  <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
                    <span aria-hidden="true">⚠</span>
                    <div>
                      <strong>These course titles are not in the catalog, so those course selections will be dropped:</strong>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                        {preview.unmatched.map((t) => <li key={t}>{t}</li>)}
                      </ul>
                      Usually a typo or a course that was renamed after the form went out.
                    </div>
                  </div>
                )}

                <div className="opt__bar">
                  <button className="btn" onClick={commit} disabled={busy || !preview.rows.length}>
                    {busy ? 'Importing…' : `Replace the student list with ${preview.rows.length} students`}
                  </button>
                  <button className="btn btn--ghost" onClick={() => setPreview(null)} disabled={busy}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      <h2 className="work__heading">
        On the student list {students && <span className="work__badge">{students.length}</span>}
      </h2>
      {withoutRanks > 0 && (
        <div className="notice notice--warning">
          <span aria-hidden="true">⚠</span>
          <div>
            {withoutRanks} student{withoutRanks === 1 ? ' has' : 's have'} no course selections. Placement
            will fill them into whatever has room.
          </div>
        </div>
      )}
      {!students?.length
        ? <Empty title="Nobody imported yet" body="Upload the ranking export once the form closes." />
        : (
          <ul className="work">
            {students.slice(0, 60).map((s) => (
              <li key={s.id} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{s.name}</div>
                  <div className="work__sub">
                    Grade {s.grade} · {s.ranks.length ? `${s.ranks.length} choices` : 'no rankings'}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      {students && students.length > 60 && (
        <p className="opt__hint">Showing the first 60 of {students.length}.</p>
      )}
    </>
  )
}
